/**
 * Normalize FastAPI's `detail` payload into a plain string so React / Sonner
 * never tries to render an object as a JSX child.
 *
 * `detail` can be:
 *   - a string  (HTTPException with a string detail)             → return as-is
 *   - an array  (Pydantic ValidationError on 422)                → join nicely
 *   - an object with `message` / `msg`                           → unwrap
 *   - anything else                                              → String()
 *
 * Returns `null` when the input is null/undefined so callers can fall back
 * to their own generic message.
 */
export function detailToMessage(detail) {
  if (detail == null) return null;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const parts = detail
      .map((d) => {
        if (d == null) return '';
        if (typeof d === 'string') return d;
        if (typeof d !== 'object') return String(d);
        const loc = Array.isArray(d.loc)
          ? d.loc.filter((p) => p !== 'body').join('.')
          : '';
        const msg = d.msg || d.message || '';
        if (loc && msg) return `${loc}: ${msg}`;
        return msg || JSON.stringify(d);
      })
      .filter(Boolean);
    return parts.length ? parts.join(' • ') : null;
  }
  if (typeof detail === 'object') {
    return detail.message || detail.msg || JSON.stringify(detail);
  }
  return String(detail);
}

/**
 * Convenience wrapper that extracts a readable error string from an axios
 * error, with sensible fallbacks. Use this in catch blocks where you want to
 * surface the server's reason to the user via a toast/banner.
 */
export function axiosErrorMessage(error, fallback = 'Something went wrong. Please try again.') {
  return (
    detailToMessage(error?.response?.data?.detail) ||
    error?.response?.data?.message ||
    error?.message ||
    fallback
  );
}
